import { useEffect, useState } from 'react';
import { api } from '../api';
import ExportButton from '../components/ExportButton';

const ORDER_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: '待接单', color: 'bg-yellow-100 text-yellow-700' },
  accepted: { label: '业务员已接', color: 'bg-blue-100 text-blue-700' },
  approved: { label: '后勤审核通过', color: 'bg-indigo-100 text-indigo-700' },
  invoiced: { label: '已开票', color: 'bg-purple-100 text-purple-700' },
  shipped: { label: '已发货', color: 'bg-teal-100 text-teal-700' },
  completed: { label: '已完成', color: 'bg-green-100 text-green-700' },
  cancelled: { label: '已取消', color: 'bg-red-100 text-red-700' },
};

type SubTab = 'customers' | 'orders' | 'inventory';

export default function Customer() {
  const [tab, setTab] = useState<SubTab>('customers');
  const [customers, setCustomers] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [custInv, setCustInv] = useState<any[]>([]);
  const [prods, setProds] = useState<any[]>([]);

  // 客户表单
  const [showCust, setShowCust] = useState(false);
  const [editCust, setEditCust] = useState<any>(null);
  const [custForm, setCustForm] = useState({ name: '', contact: '', phone: '', address: '', remark: '' });

  // 分配商品（多租户）
  const [showAssign, setShowAssign] = useState(false);
  const [assignCust, setAssignCust] = useState<any>(null);
  const [assignList, setAssignList] = useState<any[]>([]);
  const [assignSaving, setAssignSaving] = useState(false);

  // 订单表单
  const [showOrder, setShowOrder] = useState(false);
  const [orderForm, setOrderForm] = useState({ customer_id: '', items: [{ product_id: '', quantity: '', unit_price: '' }] });

  useEffect(() => {
    loadCustomers();
    api('/products').then(setProds).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'orders') loadOrders();
    if (tab === 'inventory') loadCustInv();
  }, [tab]);

  const loadCustomers = () => api('/customer/customers').then(setCustomers).catch(() => {});
  const loadOrders = () => api('/customer/orders').then(setOrders).catch(() => {});
  const loadCustInv = () => api('/customer/inventory').then(setCustInv).catch(() => {});

  // --- 客户 CRUD ---
  const saveCust = async () => {
    if (!custForm.name) return;
    if (editCust) {
      await api(`/customer/customers/${editCust.id}`, { method: 'PUT', body: JSON.stringify(custForm) });
    } else {
      await api('/customer/customers', { method: 'POST', body: JSON.stringify(custForm) });
    }
    setShowCust(false); setEditCust(null); setCustForm({ name: '', contact: '', phone: '', address: '', remark: '' }); loadCustomers();
  };
  const delCust = async (id: number) => { if (!confirm('删除该客户？')) return; await api(`/customer/customers/${id}`, { method: 'DELETE' }); loadCustomers(); };
  const openEditCust = (c: any) => { setEditCust(c); setCustForm({ name: c.name, contact: c.contact || '', phone: c.phone || '', address: c.address || '', remark: c.remark || '' }); setShowCust(true); };

  // --- 分配商品 ---
  const openAssign = async (c: any) => {
    setAssignCust(c);
    setShowAssign(true);
    setAssignSaving(false);
    try {
      const list = await api(`/customer/customers/${c.id}/products`);
      setAssignList(list.map((p: any) => ({ ...p, assigned: !!p.assigned, price: p.custom_price ?? '' })));
    } catch { setAssignList([]); }
  };
  const toggleAssign = (id: number) => setAssignList((l) => l.map((p) => (p.id === id ? { ...p, assigned: !p.assigned } : p)));
  const setAssignPrice = (id: number, v: string) => setAssignList((l) => l.map((p) => (p.id === id ? { ...p, price: v } : p)));
  const saveAssign = async () => {
    setAssignSaving(true);
    try {
      await api(`/customer/customers/${assignCust.id}/products`, {
        method: 'PUT',
        body: JSON.stringify({ items: assignList.map((p) => ({ product_id: p.id, assigned: p.assigned, price: p.price === '' ? null : p.price })) }),
      });
      setShowAssign(false);
      alert('已保存客户商品授权');
    } catch (e: any) { alert(e.message || '保存失败'); }
    finally { setAssignSaving(false); }
  };
  const assignCount = assignList.filter((p) => p.assigned).length;

  // --- 订单 ---
  const createOrder = async () => {
    if (!orderForm.customer_id) return alert('请选择客户');
    const validItems = orderForm.items.filter((it) => it.product_id && Number(it.quantity) > 0);
    if (validItems.length === 0) return alert('请至少添加一个明细');
    await api('/customer/orders', {
      method: 'POST',
      body: JSON.stringify({
        customer_id: Number(orderForm.customer_id),
        items: validItems.map((it) => ({
          product_id: Number(it.product_id),
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price) || undefined,
        })),
      }),
    });
    setShowOrder(false); setOrderForm({ customer_id: '', items: [{ product_id: '', quantity: '', unit_price: '' }] }); loadOrders();
  };

  const approveOrder = async (id: number) => {
    if (!confirm('确认后勤审核通过？')) return;
    try { await api(`/customer/orders/${id}/approve`, { method: 'POST' }); loadOrders(); } catch (e: any) { alert(e.message); }
  };

  const delOrder = async (id: number) => { if (!confirm('删除该报货单？')) return; await api(`/customer/orders/${id}`, { method: 'DELETE' }); loadOrders(); };

  const addOrderItem = () => setOrderForm({ ...orderForm, items: [...orderForm.items, { product_id: '', quantity: '', unit_price: '' }] });
  const removeOrderItem = (i: number) => setOrderForm({ ...orderForm, items: orderForm.items.filter((_, idx) => idx !== i) });

  const orderTotal = orderForm.items.reduce((sum, it) => {
    const prod = prods.find((p) => p.id === Number(it.product_id));
    return sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || prod?.price || 0);
  }, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">客户管理与报货</h2>
          <p className="text-sm text-slate-500 mt-1">客户档案 → 报货(业务员接单→后勤审核→开票发货) → 客户库存追踪</p>
        </div>
      </div>

      {/* Tab */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { key: 'customers' as const, label: '👤 客户列表' },
          { key: 'orders' as const, label: '📦 客户报货单' },
          { key: 'inventory' as const, label: '📊 客户库存' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-1.5 rounded-md text-sm transition ${tab === t.key ? 'bg-white shadow font-medium' : 'hover:bg-slate-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== 客户列表 ===== */}
      {tab === 'customers' && (
        <>
          <div className="flex justify-end">
            <button onClick={() => { setEditCust(null); setCustForm({ name: '', contact: '', phone: '', address: '', remark: '' }); setShowCust(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700 text-sm">
              + 新增客户
            </button>
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">名称</th><th>联系人</th><th>电话</th><th>地址</th><th>备注</th><th className="w-28">操作</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3">{c.contact || '—'}</td>
                    <td className="p-3">{c.phone || '—'}</td>
                    <td className="p-3 text-xs max-w-[200px] truncate">{c.address || '—'}</td>
                    <td className="p-3 text-xs text-slate-400">{c.remark || '—'}</td>
                    <td className="p-3 space-x-2">
                      <button onClick={() => openEditCust(c)} className="text-brand-600 hover:underline text-xs">编辑</button>
                      <button onClick={() => openAssign(c)} className="text-indigo-600 hover:underline text-xs">分配商品</button>
                      <button onClick={() => delCust(c.id)} className="text-red-500 hover:underline text-xs">删除</button>
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">暂无客户</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== 客户报货单 ===== */}
      {tab === 'orders' && (
        <>
          <div className="flex justify-end gap-2">
            <ExportButton
              filename="客户报货单"
              fetchData={() => api('/customer/orders')}
              columns={[
                { header: '订单号', key: 'order_no' },
                { header: '客户', key: 'customer_name' },
                { header: '金额', key: 'total_amount' },
                { header: '状态', key: 'status', format: (r) => ORDER_STATUS[r.status]?.label || r.status },
                { header: '创建时间', key: 'created_at' },
              ]}
            />
            <button onClick={() => { setOrderForm({ customer_id: '', items: [{ product_id: '', quantity: '', unit_price: '' }] }); setShowOrder(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700 text-sm">
              + 新建报货单
            </button>
          </div>

          {/* 统计卡片 */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: '总单数', value: orders.length },
              { label: '待接单', value: orders.filter((o) => o.status === 'pending').length, color: 'text-yellow-600' },
              { label: '待审核', value: orders.filter((o) => o.status === 'accepted').length, color: 'text-blue-600' },
              { label: '总金额', value: '¥' + orders.reduce((s, o) => s + (o.total_amount || 0), 0).toLocaleString(), color: 'text-green-600' },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-xl shadow p-4">
                <div className="text-xs text-slate-500">{c.label}</div>
                <div className={`text-xl font-bold mt-1 ${c.color || ''}`}>{c.value}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">订单号</th><th>客户</th><th>金额</th><th>状态</th><th>创建时间</th><th className="w-32">操作</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs">{o.order_no}</td>
                    <td className="p-3 font-medium">{o.customer_name || '—'}</td>
                    <td className="p-3 font-medium text-green-600">¥{(o.total_amount || 0).toLocaleString()}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${ORDER_STATUS[o.status]?.color || 'bg-gray-100'}`}>
                        {ORDER_STATUS[o.status]?.label || o.status}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-slate-500">{(o.created_at || '').split(' ')[0]}</td>
                    <td className="p-3 space-x-2">
                      {o.status === 'accepted' && (
                        <button onClick={() => approveOrder(o.id)} className="text-green-600 hover:underline text-xs font-medium">后勤审核</button>
                      )}
                      {['pending', 'accepted'].includes(o.status) && (
                        <button onClick={() => delOrder(o.id)} className="text-red-500 hover:underline text-xs">删除</button>
                      )}
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">暂无报货单</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== 客户库存 ===== */}
      {tab === 'inventory' && (
        <>
          <div className="flex justify-end mb-3">
            <ExportButton
              filename="客户库存"
              fetchData={() => api('/customer/inventory')}
              columns={[
                { header: '客户', key: 'customer_name' },
                { header: '联系人', key: 'contact' },
                { header: '电话', key: 'phone' },
                { header: '产品', key: 'product_name' },
                { header: '编码', key: 'code' },
                { header: '单位', key: 'unit' },
                { header: '累计采购量', key: 'total_purchased' },
                { header: '当前库存', key: 'current_stock' },
              ]}
            />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 mb-3">
            💡 客户库存视图：展示各客户的累计采购量和当前持有库存。当库存低于安全线时会显示预警。
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">客户</th><th>联系人</th><th>电话</th><th>累计采购量</th><th>当前库存</th><th>状态</th>
                </tr>
              </thead>
              <tbody>
                {custInv.map((cv) => {
                  const isLow = cv.current_stock < 10;
                  return (
                    <tr key={cv.customer_id} className={`border-t hover:bg-slate-50 ${isLow ? 'bg-red-50' : ''}`}>
                      <td className="p-3 font-medium">{cv.customer_name}</td>
                      <td className="p-3 text-xs">{cv.contact || '—'}</td>
                      <td className="p-3 text-xs">{cv.phone || '—'}</td>
                      <td className="p-3">{cv.total_purchased || 0}</td>
                      <td className="p-3 font-bold">{cv.current_stock ?? 0}</td>
                      <td className="p-3">
                        {isLow ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 animate-pulse">⚠️ 库存不足</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">正常</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {custInv.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">暂无数据</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 客户弹窗 */}
      {showCust && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCust(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">{editCust ? '编辑客户' : '新增客户'}</h3>
            <div className="space-y-3">
              <input className="w-full border rounded-lg px-3 py-2" placeholder="客户名称 *" value={custForm.name} onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="联系人" value={custForm.contact} onChange={(e) => setCustForm({ ...custForm, contact: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="电话" value={custForm.phone} onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="地址" value={custForm.address} onChange={(e) => setCustForm({ ...custForm, address: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" placeholder="备注" value={custForm.remark} onChange={(e) => setCustForm({ ...custForm, remark: e.target.value })} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowCust(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={saveCust} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 报货单弹窗 */}
      {showOrder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowOrder(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">新建客户报货单</h3>
            <select className="w-full border rounded-lg px-3 py-2 mb-4" value={orderForm.customer_id} onChange={(e) => setOrderForm({ ...orderForm, customer_id: e.target.value })}>
              <option value="">选择客户</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.contact || ''})</option>)}
            </select>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm">报货明细</span>
                <button onClick={addOrderItem} className="text-xs text-brand-600 hover:underline">+ 添加一行</button>
              </div>
              {orderForm.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select className="col-span-4 border rounded px-2 py-1.5 text-sm" value={it.product_id} onChange={(e) => { const cp = [...orderForm.items]; cp[i].product_id = e.target.value; setOrderForm({ ...orderForm, items: cp }); }}>
                    <option value="">选择产品</option>
                    {prods.map((p) => <option key={p.id} value={p.id}>{p.name} (¥{p.price})</option>)}
                  </select>
                  <input className="col-span-2 border rounded px-2 py-1.5 text-sm" type="number" placeholder="数量" value={it.quantity} onChange={(e) => { const cp = [...orderForm.items]; cp[i].quantity = e.target.value; setOrderForm({ ...orderForm, items: cp }); }} />
                  <input className="col-span-3 border rounded px-2 py-1.5 text-sm" type="number" placeholder="单价" value={it.unit_price} onChange={(e) => { const cp = [...orderForm.items]; cp[i].unit_price = e.target.value; setOrderForm({ ...orderForm, items: cp }); }} />
                  <span className="col-span-2 text-sm">¥{((Number(it.quantity) || 0) * (Number(it.unit_price) || prods.find((p) => p.id === Number(it.product_id))?.price || 0)).toLocaleString()}</span>
                  <button onClick={() => removeOrderItem(i)} className="col-span-1 text-red-500">×</button>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mb-4 p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">合计：</span>
              <span className="text-xl font-bold text-green-600">¥{orderTotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowOrder(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={createOrder} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">提交</button>
            </div>
          </div>
        </div>
      )}

      {/* 分配商品弹窗（多租户：给客户释放可售商品 + 专属价） */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAssign(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">给「{assignCust?.name}」分配商品</h3>
              <span className="text-xs text-slate-500">已选 {assignCount} 项</span>
            </div>
            <div className="text-xs text-slate-500 mb-3">
              勾选后该客户小程序端仅能看到这些商品；留空专属价则沿用全局价。全部取消勾选 = 该客户可看全部商品。
            </div>
            <div className="overflow-auto flex-1 border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr><th className="p-2 w-12 text-center">分配</th><th className="p-2 text-left">商品</th><th className="p-2 text-left">编码</th><th className="p-2 text-left">全局价</th><th className="p-2 text-left w-32">客户专属价</th></tr>
                </thead>
                <tbody>
                  {assignList.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-slate-50">
                      <td className="p-2 text-center">
                        <input type="checkbox" checked={!!p.assigned} onChange={() => toggleAssign(p.id)} className="w-4 h-4" />
                      </td>
                      <td className="p-2 font-medium">{p.name}</td>
                      <td className="p-2 text-xs text-slate-500">{p.code}</td>
                      <td className="p-2 text-slate-500">¥{p.price}</td>
                      <td className="p-2">
                        <input
                          type="number"
                          placeholder="沿用全局"
                          disabled={!p.assigned}
                          value={p.price ?? ''}
                          onChange={(e) => setAssignPrice(p.id, e.target.value)}
                          className="w-full border rounded px-2 py-1 text-sm disabled:bg-slate-100"
                        />
                      </td>
                    </tr>
                  ))}
                  {assignList.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">暂无商品</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAssign(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={saveAssign} disabled={assignSaving} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
                {assignSaving ? '保存中…' : '保存授权'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
